import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Heart, Save, UserPlus, Calendar, Star } from 'lucide-react';
import { toast } from 'sonner';
import { CandidateDiscovery } from '@/components/CandidateDiscovery';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });
const PLAN_LABEL = { free: 'Free', standard: 'Standard', premium: 'Premium', private_match: 'Private Match' };

const EMPTY_INTAKE = {
  partner_criteria: '', dealbreakers: '', age_min: '', age_max: '',
  location_pref: '', faith_pref: '', lifestyle_notes: '', availability: '', internal_notes: '',
};

export const MemberManageDialog = ({ userId, userName, onChanged, children }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [intake, setIntake] = useState(EMPTY_INTAKE);
  const [feedback, setFeedback] = useState([]);
  const [schedSearch, setSchedSearch] = useState('');
  const [schedCands, setSchedCands] = useState([]);
  const [schedSel, setSchedSel] = useState(null);
  const [schedAt, setSchedAt] = useState('');
  const [schedType, setSchedType] = useState('date');
  const [schedFormat, setSchedFormat] = useState('video');
  const [schedHostJoins, setSchedHostJoins] = useState(false);
  const [schedNotes, setSchedNotes] = useState('');
  const [grantPlanType, setGrantPlanType] = useState('premium');
  const [grantSource, setGrantSource] = useState('trial');
  const [grantDuration, setGrantDuration] = useState('30');
  const [grantCustom, setGrantCustom] = useState('');

  const grantPlan = async () => {
    let duration_days = null;
    if (grantDuration === 'custom') duration_days = parseInt(grantCustom, 10) || null;
    else if (grantDuration !== 'none') duration_days = parseInt(grantDuration, 10);
    try {
      await axios.post(`${API}/admin/users/${userId}/grant-plan`,
        { plan_type: grantPlanType, duration_days, grant_source: grantSource }, authHeaders());
      const planLabel = { standard: 'Standard', premium: 'Premium', private_match: 'Private Match' }[grantPlanType] || grantPlanType;
      toast.success(`${planLabel} granted to ${userName}`);
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to grant plan'); }
  };
  const revokePlan = async () => {
    try {
      await axios.post(`${API}/admin/users/${userId}/revoke-plan`, {}, authHeaders());
      toast.success('Plan revoked — member moved to Free');
      onChanged && onChanged();
    } catch (e) { toast.error('Failed to revoke'); }
  };

  const load = useCallback(async () => {
    try {
      const [res, fb] = await Promise.all([
        axios.get(`${API}/admin/members/${userId}`, authHeaders()),
        axios.get(`${API}/admin/feedback?about_user_id=${userId}`, authHeaders()),
      ]);
      setDetail(res.data);
      setIntake({ ...EMPTY_INTAKE, ...(res.data.intake || {}) });
      setFeedback(fb.data);
    } catch (e) { toast.error('Failed to load member'); }
  }, [userId]);

  const onOpenChange = (v) => { setOpen(v); if (v) load(); };

  const saveIntake = async () => {
    try {
      const payload = { ...intake };
      ['age_min', 'age_max'].forEach((k) => { payload[k] = payload[k] === '' ? null : Number(payload[k]); });
      await axios.put(`${API}/admin/members/${userId}/intake`, payload, authHeaders());
      toast.success('Intake saved');
    } catch (e) { toast.error('Failed to save intake'); }
  };

  const propose = async (candidateId, reason) => {
    try {
      await axios.post(`${API}/admin/proposals`, { member_id: userId, candidate_id: candidateId, reason }, authHeaders());
      toast.success('Introduction proposed to both members');
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to propose'); }
  };

  const searchSched = async () => {
    try {
      const res = await axios.get(`${API}/admin/users?search=${encodeURIComponent(schedSearch)}&limit=10`, authHeaders());
      setSchedCands(res.data.users.filter((u) => u.id !== userId));
    } catch (e) { toast.error('Search failed'); }
  };

  const scheduleDate = async () => {
    if (!schedAt) return toast.error('Pick a date & time');
    const isInterview = schedType === 'interview';
    if (!isInterview && !schedSel) return toast.error('Select the other member first');
    const participants = isInterview ? [userId] : [userId, schedSel.id];
    try {
      await axios.post(`${API}/admin/dates`, {
        participants,
        meeting_type: schedType,
        format: schedFormat,
        host_joins: schedType === 'date' ? schedHostJoins : true,
        scheduled_at: new Date(schedAt).toISOString(),
        notes: schedNotes || null,
      }, authHeaders());
      toast.success('Meeting scheduled — participants notified in their Concierge');
      setSchedSel(null); setSchedAt(''); setSchedNotes(''); setSchedCands([]); setSchedSearch('');
      setSchedType('date'); setSchedFormat('video'); setSchedHostJoins(false);
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to schedule meeting'); }
  };

  const p = detail?.profile;
  const setField = (k, v) => setIntake((prev) => ({ ...prev, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage {userName}</DialogTitle>
          <DialogDescription>Review the member, capture their matchmaking intake, and propose curated introductions.</DialogDescription>
        </DialogHeader>

        {!detail ? <p className="text-gray-500 py-8 text-center">Loading...</p> : (
          <Tabs defaultValue="profile">
            <TabsList>
              <TabsTrigger value="profile" data-testid="mm-tab-profile">Profile</TabsTrigger>
              <TabsTrigger value="intake" data-testid="mm-tab-intake">Intake</TabsTrigger>
              <TabsTrigger value="propose" data-testid="mm-tab-propose">Propose</TabsTrigger>
              <TabsTrigger value="schedule" data-testid="mm-tab-schedule">Schedule</TabsTrigger>
              <TabsTrigger value="plan" data-testid="mm-tab-plan">Plan</TabsTrigger>
              <TabsTrigger value="feedback" data-testid="mm-tab-feedback">Feedback</TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="space-y-3 pt-3">
              <div className="flex items-center gap-3">
                {p.picture ? <img src={p.picture} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 rounded-full bg-rose-100" />}
                <div>
                  <p className="text-lg font-semibold text-rose-900">{p.name}</p>
                  <p className="text-sm text-gray-500">{p.email}</p>
                  <Badge variant="outline" className="mt-1">{PLAN_LABEL[detail.subscription.plan_type]}</Badge>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-rose-50 rounded-lg p-3"><p className="text-xl font-bold text-rose-700">{detail.stats.total_matches}</p><p className="text-xs text-gray-500">Matches</p></div>
                <div className="bg-rose-50 rounded-lg p-3"><p className="text-xl font-bold text-rose-700">{detail.stats.live_matches}</p><p className="text-xs text-gray-500">Live Matches</p></div>
                <div className="bg-rose-50 rounded-lg p-3"><p className="text-xl font-bold text-rose-700">{detail.stats.service_requests}</p><p className="text-xs text-gray-500">Requests</p></div>
              </div>
              {p.bio && <div><p className="text-xs font-semibold text-gray-500 uppercase">Bio</p><p className="text-sm text-gray-700">{p.bio}</p></div>}
              {(p.city || p.country) && <p className="text-sm text-gray-600">📍 {[p.city, p.country].filter(Boolean).join(', ')}</p>}
              {p.core_values?.length > 0 && <div><p className="text-xs font-semibold text-gray-500 uppercase">Core values</p><div className="flex flex-wrap gap-1 mt-1">{p.core_values.map((v) => <Badge key={v} variant="outline">{v}</Badge>)}</div></div>}
              {p.faith_spiritual && <p className="text-sm text-gray-600"><span className="font-medium">Faith:</span> {typeof p.faith_spiritual === 'string' ? p.faith_spiritual : JSON.stringify(p.faith_spiritual)}</p>}
            </TabsContent>

            <TabsContent value="intake" className="space-y-3 pt-3" data-testid="mm-intake-form">
              <Field label="Partner criteria" value={intake.partner_criteria} onChange={(v) => setField('partner_criteria', v)} area />
              <Field label="Deal-breakers" value={intake.dealbreakers} onChange={(v) => setField('dealbreakers', v)} area />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Age min" value={intake.age_min} onChange={(v) => setField('age_min', v)} type="number" />
                <Field label="Age max" value={intake.age_max} onChange={(v) => setField('age_max', v)} type="number" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Location preference" value={intake.location_pref} onChange={(v) => setField('location_pref', v)} />
                <Field label="Faith preference" value={intake.faith_pref} onChange={(v) => setField('faith_pref', v)} />
              </div>
              <Field label="Lifestyle notes" value={intake.lifestyle_notes} onChange={(v) => setField('lifestyle_notes', v)} area />
              <Field label="Availability" value={intake.availability} onChange={(v) => setField('availability', v)} />
              <Field label="Internal notes (staff only)" value={intake.internal_notes} onChange={(v) => setField('internal_notes', v)} area />
              <Button data-testid="mm-save-intake" onClick={saveIntake} className="bg-rose-600 hover:bg-rose-700"><Save className="w-4 h-4 mr-2" /> Save Intake</Button>
            </TabsContent>

            <TabsContent value="propose" className="pt-1">
              <CandidateDiscovery clientId={userId} onPropose={propose} />
            </TabsContent>

            <TabsContent value="schedule" className="space-y-3 pt-3" data-testid="mm-schedule-form">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Meeting type</label>
                <Select value={schedType} onValueChange={(v) => { setSchedType(v); if (v === 'interview') { setSchedSel(null); setSchedFormat((f) => f === 'in_person' ? 'video' : f); } }}>
                  <SelectTrigger data-testid="sched-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interview">Interview (you + this member)</SelectItem>
                    <SelectItem value="chaperoned">Chaperoned intro (you + two members)</SelectItem>
                    <SelectItem value="date">Private date (two members)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400 mt-1">
                  {schedType === 'interview' ? 'A private 1-on-1 call between you and this member.'
                    : schedType === 'chaperoned' ? 'You host and chaperone the first meeting between two members.'
                    : 'The two members meet privately. You can optionally join to introduce them.'}
                </p>
              </div>
              {schedType !== 'interview' && (
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Other member</label>
                  {schedSel ? (
                    <div className="flex items-center justify-between border border-indigo-200 bg-indigo-50 rounded-lg p-2 mt-1" data-testid="sched-selected">
                      <div><p className="font-medium text-sm">{schedSel.name}</p><p className="text-xs text-gray-400">{schedSel.email}</p></div>
                      <Button size="sm" variant="ghost" onClick={() => setSchedSel(null)}>Change</Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2 mt-1">
                        <div className="relative flex-1">
                          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <Input data-testid="sched-candidate-search" placeholder="Search the other member..." value={schedSearch}
                            onChange={(e) => setSchedSearch(e.target.value)} className="pl-9"
                            onKeyDown={(e) => { if (e.key === 'Enter') searchSched(); }} />
                        </div>
                        <Button variant="outline" onClick={searchSched}>Search</Button>
                      </div>
                      <div className="space-y-2 max-h-[180px] overflow-y-auto mt-2">
                        {schedCands.map((c) => (
                          <div key={c.id} className="flex items-center justify-between border border-gray-100 rounded-lg p-2" data-testid={`sched-cand-${c.id}`}>
                            <div><p className="font-medium text-sm">{c.name}</p><p className="text-xs text-gray-400">{c.email}</p></div>
                            <Button size="sm" variant="outline" data-testid={`sched-pick-${c.id}`} onClick={() => { setSchedSel(c); setSchedCands([]); }}>Select</Button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Date &amp; time</label>
                <Input data-testid="sched-datetime" type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Format</label>
                <Select value={schedFormat} onValueChange={setSchedFormat}>
                  <SelectTrigger data-testid="sched-format" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Video call (in-app)</SelectItem>
                    <SelectItem value="audio">Audio only (in-app)</SelectItem>
                    {schedType === 'date' && <SelectItem value="in_person">In person</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {schedType === 'date' && schedFormat !== 'in_person' && (
                <label className="flex items-center gap-2 text-sm text-gray-600" data-testid="sched-host-joins">
                  <input type="checkbox" checked={schedHostJoins} onChange={(e) => setSchedHostJoins(e.target.checked)} className="rounded" />
                  Join the call to introduce them (you'll be the host)
                </label>
              )}
              <Field label="Notes (shared with participants)" value={schedNotes} onChange={setSchedNotes} area />
              <Button data-testid="mm-schedule-submit" onClick={scheduleDate} className="bg-indigo-600 hover:bg-indigo-700"><Calendar className="w-4 h-4 mr-2" /> Schedule Meeting</Button>
            </TabsContent>

            <TabsContent value="plan" className="space-y-3 pt-3" data-testid="mm-plan-form">
              <p className="text-xs text-gray-500">Grant a plan manually — e.g. a trial, a live-event subscriber, or a complimentary gift. Optionally set an expiry; it auto-downgrades to Free when it ends.</p>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Plan</label>
                <Select value={grantPlanType} onValueChange={setGrantPlanType}>
                  <SelectTrigger data-testid="grant-plan-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="private_match">Private Match</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Type</label>
                <Select value={grantSource} onValueChange={setGrantSource}>
                  <SelectTrigger data-testid="grant-source" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="live_subscriber">Live Subscriber</SelectItem>
                    <SelectItem value="complimentary">Complimentary (gift)</SelectItem>
                    <SelectItem value="manual">Manual / Paid offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Duration</label>
                <Select value={grantDuration} onValueChange={setGrantDuration}>
                  <SelectTrigger data-testid="grant-duration" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="custom">Custom days…</SelectItem>
                    <SelectItem value="none">No expiry</SelectItem>
                  </SelectContent>
                </Select>
                {grantDuration === 'custom' && (
                  <Input data-testid="grant-custom-days" type="number" min="1" value={grantCustom} onChange={(e) => setGrantCustom(e.target.value)} className="mt-2" placeholder="Number of days" />
                )}
              </div>
              <div className="flex gap-2 pt-1">
                <Button data-testid="grant-plan-btn" onClick={grantPlan} className="bg-indigo-600 hover:bg-indigo-700">Grant Plan</Button>
                <Button data-testid="revoke-plan-btn" onClick={revokePlan} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">Revoke to Free</Button>
              </div>
            </TabsContent>

            <TabsContent value="feedback" className="space-y-3 pt-3" data-testid="mm-feedback-list">
              {feedback.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No post-date feedback about this member yet.</p>
              ) : feedback.map((f) => (
                <div key={f.id} className="border border-rose-100 rounded-lg p-3" data-testid={`feedback-${f.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= f.rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} />)}
                    </div>
                    <Badge variant={f.would_meet_again ? 'default' : 'outline'} className={f.would_meet_again ? 'bg-green-600' : ''}>
                      {f.would_meet_again ? 'Wants to meet again' : 'No second date'}
                    </Badge>
                  </div>
                  {f.comments && <p className="text-sm text-gray-700 mt-2">{f.comments}</p>}
                  <p className="text-xs text-gray-400 mt-1">From {f.from_name || 'a member'} · {new Date(f.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, value, onChange, area, type = 'text' }) => (
  <div>
    <label className="text-xs font-semibold text-gray-500 uppercase">{label}</label>
    {area
      ? <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={2} className="resize-none mt-1" />
      : <Input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className="mt-1" />}
  </div>
);

export default MemberManageDialog;
